<template>
  <div class="bill-revenue-report-table">
    <div
      v-if="formattedLastSyncAt"
      class="d-flex justify-content-end align-items-center mb-2"
    >
      <small
        v-if="formattedLastSyncAt"
        class="text-muted"
      >
        <i class="flaticon2-time mr-1"></i>
        Cập nhật lần cuối: <strong>{{ formattedLastSyncAt }}</strong>
      </small>
    </div>
    <b-row
      v-if="loading"
      class="mb-3"
    >
      <b-col
        v-for="n in 4"
        :key="'summary-skeleton-' + n"
        cols="6"
        md="3"
        class="mb-3 mb-md-0"
      >
        <b-card class="summary-card h-100">
          <b-skeleton
            animation="wave"
            width="55%"
            class="mb-2"
          />
          <b-skeleton
            animation="wave"
            height="1.75rem"
            width="85%"
          />
        </b-card>
      </b-col>
    </b-row>
    <b-row
      v-else
      class="mb-3"
    >
      <b-col
        cols="6"
        md="3"
        class="mb-3 mb-md-0"
      >
        <b-card class="summary-card h-100">
          <h6 class="text-muted mb-1">Doanh thu</h6>
          <h4 class="mb-0 text-primary">{{ formatMoney(summaryDisplay.totalDoanhThu) }}đ</h4>
        </b-card>
      </b-col>
      <b-col
        cols="6"
        md="3"
        class="mb-3 mb-md-0"
      >
        <b-card class="summary-card h-100">
          <h6 class="text-muted mb-1">Giá vốn</h6>
          <h4 class="mb-0 text-warning">
            {{ formatMoney(summaryDisplay.totalChiPhiGiaVonHangHoa) }}đ
          </h4>
        </b-card>
      </b-col>
      <b-col
        cols="6"
        md="3"
        class="mb-3 mb-md-0"
      >
        <b-card class="summary-card h-100">
          <h6 class="text-muted mb-1">Lợi nhuận gộp</h6>
          <h4 class="mb-0 text-success">{{ formatMoney(summaryDisplay.totalLoiNhuanGop) }}đ</h4>
        </b-card>
      </b-col>
      <b-col
        cols="6"
        md="3"
      >
        <b-card class="summary-card h-100">
          <h6 class="text-muted mb-1">Lợi nhuận ròng</h6>
          <h4
            class="mb-0"
            :class="summaryDisplay.totalLoiNhuanRong >= 0 ? 'text-success' : 'text-danger'"
          >
            {{ formatMoney(summaryDisplay.totalLoiNhuanRong) }}đ
          </h4>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="mb-3 mt-4 pt-4 align-items-center">
      <b-col
        class="d-flex align-items-center"
        style="gap: 8px"
      >
        <b-dropdown
          size="sm"
          variant="outline-secondary"
          no-caret
          menu-class="column-select-menu"
        >
          <template #button-content>
            <i class="fas fa-columns mr-1"></i>
            Chọn cột hiển thị
            <b-badge
              variant="primary"
              class="ml-1"
              >{{ visibleFieldsCount }}</b-badge
            >
          </template>
          <b-dropdown-form class="column-select-form">
            <b-form-checkbox
              :checked="allVisible"
              :indeterminate="isIndeterminate"
              @change="toggleAllColumns"
              class="mb-2 font-weight-bold"
            >
              Tất cả
            </b-form-checkbox>
            <hr class="my-1" />
            <div class="column-picker-grid">
              <b-form-checkbox
                v-for="col in allFields"
                :key="col.key"
                v-model="col.visible"
                :disabled="col.required"
                @change="onColumnVisibilityChange"
                class="column-picker-item mb-1"
              >
                {{ col.label }}
                <small
                  v-if="col.required"
                  class="text-muted"
                  >(bắt buộc)</small
                >
              </b-form-checkbox>
            </div>
          </b-dropdown-form>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item-button
            @click="resetColumns"
            variant="link"
            class="text-center"
          >
            <i class="fas fa-undo mr-1"></i> Đặt lại mặc định
          </b-dropdown-item-button>
        </b-dropdown>

        <b-button
          size="sm"
          variant="outline-info"
          @click="showFormulaModal = true"
          class="ml-2"
        >
          <i class="fas fa-info-circle mr-1"></i>
          Cách tính chỉ số
        </b-button>
      </b-col>
    </b-row>

    <FormulaExplanationModal v-model="showFormulaModal" />

    <div class="table-container">
      <b-table
        :items="items"
        :fields="visibleFields"
        :busy="loading"
        :style="{ minWidth: tableMinWidth + 'px' }"
        class="table-bordered table-hover bill-data-table"
        :small="true"
      >
        <template v-slot:table-busy>
          <vcl-table
            :speed="5"
            :animate="true"
            :columns="10"
          ></vcl-table>
        </template>

        <template v-slot:cell(stt)="row">
          <div class="text-center">
            {{ (currentPage - 1) * perPage + row.index + 1 }}
          </div>
        </template>

        <template v-slot:cell(billNumber)="row">
          <div class="text-center">
            <span
              class="text-primary cursor-pointer hover-link"
              @click="openBillDetail(row.item)"
            >
              {{ row.item.billNumber }}
            </span>
          </div>
        </template>

        <template v-slot:cell(reportDate)="row">
          <div class="text-center">{{ formatDate(row.item.reportDate) }}</div>
        </template>

        <template v-slot:cell(store)="row">
          <div>{{ row.item.store?.name || '—' }}</div>
          <div
            class="text-danger pt-2"
            v-b-tooltip.hover.top
            title="Chi nhánh bán"
            v-if="
              row.item.billType === BILL_TYPE.REFUND_INVOICE &&
              row.item.originalStore?.name !== row.item.store?.name
            "
          >
            <i class="fas fa-shopping-basket"></i>
            {{ row.item.originalStore?.name || '—' }}
          </div>
        </template>

        <template v-slot:cell(billType)="row">
          <div class="text-center">
            <b-badge :variant="billTypeBadgeVariant(row.item.billType)">
              {{ billTypeName(row.item.billType) }}
            </b-badge>
          </div>
        </template>

        <template v-slot:cell(tienHangThucBan)="row">
          <div class="text-right">{{ formatMoney(row.item.tienHangThucBan) }}</div>
        </template>

        <template v-slot:cell(chietKhau)="row">
          <div class="text-right">{{ formatMoney(row.item.chietKhau) }}</div>
        </template>

        <template v-slot:cell(thueVAT)="row">
          <div class="text-right">{{ formatMoney(row.item.thueVAT) }}</div>
        </template>

        <template v-slot:cell(phiGiaoHangKhach)="row">
          <div class="text-right">{{ formatMoney(row.item.phiGiaoHangKhach) }}</div>
        </template>

        <template v-slot:cell(doanhThu)="row">
          <div class="text-right font-weight-bold text-primary">
            {{ formatMoney(row.item.doanhThu) }}
          </div>
        </template>

        <template v-slot:cell(chiPhiGiaVonHangHoa)="row">
          <div class="text-right">{{ formatMoney(row.item.chiPhiGiaVonHangHoa) }}</div>
        </template>

        <template v-slot:cell(thanhToanDiem)="row">
          <div class="text-right">{{ formatMoney(row.item.thanhToanDiem) }}</div>
        </template>

        <template v-slot:cell(phiGiaoHangShop)="row">
          <div class="text-right">{{ formatMoney(row.item.phiGiaoHangShop) }}</div>
        </template>

        <template v-slot:cell(chiPhi)="row">
          <div class="text-right">{{ formatMoney(row.item.chiPhi) }}</div>
        </template>

        <template v-slot:cell(loiNhuan)="row">
          <div
            class="text-right font-weight-bold"
            :class="row.item.loiNhuan >= 0 ? 'text-success' : 'text-danger'"
          >
            {{ formatMoney(row.item.loiNhuan) }}
          </div>
        </template>

        <template v-slot:cell(loiNhuanGop)="row">
          <div
            class="text-right font-weight-bold"
            :class="row.item.loiNhuanGop >= 0 ? 'text-success' : 'text-danger'"
          >
            {{ formatMoney(row.item.loiNhuanGop) }}
          </div>
        </template>

        <template v-slot:cell(loiNhuanRong)="row">
          <div
            class="text-right font-weight-bold"
            :class="row.item.loiNhuanRong >= 0 ? 'text-success' : 'text-danger'"
          >
            {{ formatMoney(row.item.loiNhuanRong) }}
          </div>
        </template>
      </b-table>
    </div>

    <b-row class="mt-4">
      <b-col>
        <p
          class="mt-3 text-dark"
          style="font-weight: 500"
        >
          Tổng số: {{ convertPrice(totalCount) }}
        </p>
      </b-col>
      <b-col>
        <BPaginationNavCustom
          :number-of-pages="totalPages"
          :current-page="currentPage"
          :total-page="totalPages"
          @page-change="onPageChange"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import moment from 'moment'
import { convertPrice } from '@/utils/common'
import { VclTable } from 'vue-content-loading'
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue'
import { BILL_TYPE_NAME } from '@/utils/enum-name'
import { BILL_TYPE } from '@/utils/enum'
import FormulaExplanationModal from './FormulaExplanationModal.vue'

const STORAGE_KEY = 'billRevenueReport_visibleColumns'

export default {
  name: 'BillRevenueReportTable',
  components: {
    VclTable,
    BPaginationNavCustom,
    FormulaExplanationModal,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    summary: {
      type: Object,
      default: null,
    },
    lastSyncAt: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      BILL_TYPE,
      showFormulaModal: false,
      allFields: [
        {
          key: 'stt',
          label: 'STT',
          thStyle: { textAlign: 'center', fontWeight: 600, width: '50px', minWidth: '50px' },
          visible: true,
          required: true,
          defaultVisible: true,
        },
        {
          key: 'billNumber',
          label: 'Mã HĐ',
          sortable: true,
          thStyle: { fontWeight: 600, width: '100px', minWidth: '80px', textAlign: 'center' },
          visible: true,
          required: true,
          defaultVisible: true,
        },
        {
          key: 'reportDate',
          label: 'Ngày',
          sortable: true,
          thStyle: { fontWeight: 600, width: '100px', minWidth: '100px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'store',
          label: 'Chi nhánh',
          sortable: true,
          thStyle: { fontWeight: 600, width: '120px', minWidth: '100px', textAlign: 'center' },
          visible: false,
          required: false,
          defaultVisible: false,
        },
        {
          key: 'billType',
          label: 'Loại đơn',
          sortable: true,
          thStyle: { fontWeight: 600, width: '120px', minWidth: '100px', textAlign: 'center' },
          visible: false,
          required: false,
          defaultVisible: false,
        },
        {
          key: 'tienHangThucBan',
          label: 'Tiền hàng thực bán',
          sortable: true,
          thStyle: { fontWeight: 600, width: '150px', minWidth: '130px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'chietKhau',
          label: 'Chiết khấu',
          sortable: true,
          thStyle: { fontWeight: 600, width: '110px', minWidth: '100px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'thueVAT',
          label: 'Thuế VAT',
          sortable: true,
          thStyle: { fontWeight: 600, width: '110px', minWidth: '100px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'phiGiaoHangKhach',
          label: 'Phí GH (khách)',
          sortable: true,
          thStyle: { fontWeight: 600, width: '120px', minWidth: '110px', textAlign: 'center' },
          visible: false,
          required: false,
          defaultVisible: false,
        },
        {
          key: 'chiPhiGiaVonHangHoa',
          label: 'Giá vốn hàng hóa',
          sortable: true,
          thStyle: { fontWeight: 600, width: '150px', minWidth: '130px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'thanhToanDiem',
          label: 'Thanh toán điểm',
          sortable: true,
          thStyle: { fontWeight: 600, width: '130px', minWidth: '120px', textAlign: 'center' },
          visible: false,
          required: false,
          defaultVisible: false,
        },
        {
          key: 'phiGiaoHangShop',
          label: 'Phí GH (shop)',
          sortable: true,
          thStyle: { fontWeight: 600, width: '120px', minWidth: '110px', textAlign: 'center' },
          visible: false,
          required: false,
          defaultVisible: false,
        },
        {
          key: 'chiPhi',
          label: 'Tổng chi phí',
          sortable: true,
          thStyle: { fontWeight: 600, width: '120px', minWidth: '110px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'doanhThu',
          label: 'Doanh thu',
          sortable: true,
          thStyle: { fontWeight: 600, width: '130px', minWidth: '120px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'loiNhuan',
          label: 'Lợi nhuận',
          sortable: true,
          thStyle: { fontWeight: 600, width: '120px', minWidth: '110px', textAlign: 'center' },
          visible: false,
          required: false,
          defaultVisible: false,
        },
        {
          key: 'loiNhuanGop',
          label: 'Lợi nhuận gộp',
          sortable: true,
          thStyle: { fontWeight: 600, width: '130px', minWidth: '120px', textAlign: 'center' },
          visible: true,
          required: false,
          defaultVisible: true,
        },
        {
          key: 'loiNhuanRong',
          label: 'Lợi nhuận ròng',
          sortable: true,
          thStyle: { fontWeight: 600, width: '130px', minWidth: '120px', textAlign: 'center' },
          visible: false,
          required: false,
          defaultVisible: false,
        },
      ],
    }
  },
  computed: {
    summaryDisplay() {
      if (this.summary) return this.summary
      return {
        totalDoanhThu: 0,
        totalChiPhiGiaVonHangHoa: 0,
        totalLoiNhuanGop: 0,
        totalLoiNhuanRong: 0,
      }
    },
    formattedLastSyncAt() {
      if (!this.lastSyncAt) return null
      return moment(this.lastSyncAt).format('HH:mm DD/MM/YYYY')
    },
    visibleFields() {
      return this.allFields.filter((f) => f.visible)
    },
    visibleFieldsCount() {
      return this.visibleFields.length
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage) || 1
    },
    tableMinWidth() {
      return this.visibleFields.reduce((sum, f) => {
        const w = parseInt(f.thStyle?.minWidth || f.thStyle?.width || '100px')
        return sum + w
      }, 0)
    },
    toggleableFields() {
      return this.allFields.filter((f) => !f.required)
    },
    allVisible() {
      return this.toggleableFields.every((f) => f.visible)
    },
    isIndeterminate() {
      const visibleCount = this.toggleableFields.filter((f) => f.visible).length
      return visibleCount > 0 && visibleCount < this.toggleableFields.length
    },
  },
  created() {
    this.loadColumnVisibility()
  },
  methods: {
    convertPrice,
    billTypeName(type) {
      return BILL_TYPE_NAME[type] || '-'
    },
    billTypeBadgeVariant(type) {
      if (type === BILL_TYPE.REFUND_INVOICE) return 'danger'
      if (type === BILL_TYPE.WHOLESALE_INVOICE) return 'info'
      return 'success'
    },
    openBillDetail({ billNumber, billType }) {
      let routerName = 'update-bill'
      switch (billType) {
        case BILL_TYPE.RETAIL_INVOICE:
          routerName = 'update-bill'
          break
        case BILL_TYPE.WHOLESALE_INVOICE:
          routerName = 'update-wholesale-invoice'
          break
        case BILL_TYPE.REFUND_INVOICE:
          routerName = 'update-refund-bill'
          break
      }

      const routeData = this.$router.resolve({
        name: routerName,
        query: { id: billNumber, billNumber },
      })
      window.open(routeData.href, '_blank')
    },
    formatMoney(number) {
      if (number === null || number === undefined) return '0'
      return new Intl.NumberFormat('vi-VN').format(number)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY')
    },
    onPageChange(page) {
      this.$emit('page-change', page)
    },
    onColumnVisibilityChange() {
      this.saveColumnVisibility()
    },
    saveColumnVisibility() {
      const visibilityMap = {}
      this.allFields.forEach((f) => {
        visibilityMap[f.key] = f.visible
      })
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visibilityMap))
    },
    loadColumnVisibility() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY)
        if (saved) {
          const visibilityMap = JSON.parse(saved)
          this.allFields.forEach((f) => {
            if (visibilityMap.hasOwnProperty(f.key)) {
              f.visible = f.required ? true : visibilityMap[f.key]
            }
          })
        }
      } catch (e) {
        console.error('Error loading column visibility:', e)
      }
    },
    resetColumns() {
      this.allFields.forEach((f) => {
        f.visible = f.defaultVisible
      })
      this.saveColumnVisibility()
    },
    toggleAllColumns(checked) {
      this.allFields.forEach((f) => {
        if (!f.required) f.visible = checked
      })
      this.saveColumnVisibility()
    },
  },
}
</script>

<style lang="scss" scoped>
.bill-revenue-report-table {
  width: 100%;
}

.summary-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h6 {
    font-size: 0.85rem;
  }
  h4 {
    font-size: 1.25rem;
  }
}

.hover-link {
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.table-container {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: unset;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 1rem;
}

::v-deep .bill-data-table {
  width: auto;
  min-width: 100%;
  table-layout: fixed;

  th,
  td {
    padding: 0.6rem 0.75rem;
    vertical-align: middle;
    border-top: 1px solid #ecf0f3;
    white-space: nowrap;
  }

  th {
    background-color: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 2;
  }
}

#scroll-table ::v-deep table th:first-child,
#scroll-table ::v-deep table th:nth-child(2),
#scroll-table ::v-deep table td:first-child,
#scroll-table ::v-deep table td:nth-child(2) {
  position: static !important;
  left: auto !important;
  z-index: auto !important;
}

#scroll-table ::v-deep table {
  table-layout: fixed;
  width: auto;
  min-width: 100%;
}

@media (max-width: 767px) {
  .summary-card h4 {
    font-size: 1rem;
  }
}
</style>

<style scoped lang="scss">
.column-select-menu {
  min-width: 440px;
  max-width: min(520px, 96vw);
  max-height: 400px;
  overflow-y: auto;
}

.column-select-form {
  padding: 0.5rem 1rem;
}

.column-picker-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.15rem 1.25rem;
  align-items: start;
}

.column-picker-item {
  margin-bottom: 0;
  white-space: nowrap;
}
</style>
